import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Select,
  MenuItem,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";

const formatMoney = (v) => Number(v || 0).toFixed(2);

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("access") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    "";

  return token ? { Authorization: `Bearer ${token}` } : {};
};

const normalizeDecimalInput = (raw) => {
  let s = String(raw ?? "");
  s = s.replace(/,/g, ".");
  s = s.replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
    const parts = s.split(".");
    s = `${parts[0]}.${(parts[1] || "").slice(0, 6)}`;
  }
  return s;
};

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(String(v).replace(/,/g, "."));
  return Number.isFinite(n) ? n : 0;
};

const round6 = (v) => Number(Number(v || 0).toFixed(6));
const round2 = (v) => Number(Number(v || 0).toFixed(2));
const toCents = (v) => Math.round(Number(v || 0) * 100);
const centsToMoney = (c) => Number((Number(c || 0) / 100).toFixed(2));

const normalizeAccounts = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.accounts)) return data.accounts;
  if (Array.isArray(data.cuentas)) return data.cuentas;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

export default function PurchaseAccounts({ rows, setRows, onAccountsDataChange }) {
  const searchBoxRef = useRef(null);
  const safeRows = Array.isArray(rows) ? rows : [];

  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [descripcionLinea, setDescripcionLinea] = useState("");
  const [valor, setValor] = useState("");
  const [ivaPorcentaje, setIvaPorcentaje] = useState(15);
  const [descuentoPct, setDescuentoPct] = useState("0");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const calcRow = (r) => {
    const brutoExacto = round6(toNum(r.valor));
    const dPct = toNum(r.descuentoPct);
    const ivaPct = Number(r.iva_porcentaje ?? 15);

    const descExacto = round6((brutoExacto * dPct) / 100);
    const netoExacto = round6(brutoExacto - descExacto);

    const bruto = round2(brutoExacto);
    const descTotal = round2(descExacto);
    const neto = round2(netoExacto);

    const brutoC = toCents(bruto);
    const descC = toCents(descTotal);
    const netoC = toCents(neto);

    const ivaExacto =
      ivaPct === 15
        ? round6(netoExacto * 0.15)
        : ivaPct === 5
        ? round6(netoExacto * 0.05)
        : 0;

    const ivaValor = round2(ivaExacto);
    const ivaC = toCents(ivaValor);
    const totalLineaC = netoC + ivaC;
    const totalLinea = centsToMoney(totalLineaC);

    return {
      brutoC,
      descC,
      netoC,
      ivaC,
      totalLineaC,
      bruto,
      descTotal,
      neto,
      ivaValor,
      totalLinea,
      ivaPct,
    };
  };

  const totals = useMemo(() => {
    return safeRows.reduce(
      (acc, r) => {
        const calc = calcRow(r);

        if (calc.ivaPct === 15) {
          acc.neto15C += calc.netoC;
          acc.iva15C += calc.ivaC;
        } else if (calc.ivaPct === 5) {
          acc.neto5C += calc.netoC;
          acc.iva5C += calc.ivaC;
        } else if (calc.ivaPct === 999) {
          acc.netoNoObjC += calc.netoC;
        } else {
          acc.neto0C += calc.netoC;
        }

        acc.descuentoC += calc.descC;
        acc.totalC += calc.totalLineaC;

        return acc;
      },
      {
        neto15C: 0,
        neto5C: 0,
        neto0C: 0,
        netoNoObjC: 0,
        iva15C: 0,
        iva5C: 0,
        descuentoC: 0,
        totalC: 0,
      }
    );
  }, [safeRows]);

  const accountsPayload = useMemo(() => {
    return safeRows.map((r) => {
      const calc = calcRow(r);

      return {
        cuenta_id: r.cuenta_id,
        cuenta_contable_id: r.cuenta_id,
        cuenta_codigo: r.cuenta_codigo || "",
        cuenta_nombre: r.cuenta_nombre || "",
        descripcion: r.descripcion || "",
        valor: formatMoney(calc.bruto),
        subtotal: formatMoney(calc.bruto),
        descuentoPct: r.descuentoPct ?? "0",
        descuento_pct: r.descuentoPct ?? "0",
        descuento_valor: formatMoney(calc.descTotal),
        base_imponible: formatMoney(calc.neto),
        iva_porcentaje: Number(r.iva_porcentaje ?? 0),
        iva_valor: formatMoney(calc.ivaValor),
        total: formatMoney(calc.totalLinea),
        total_linea: formatMoney(calc.totalLinea),
      };
    });
  }, [safeRows]);

  useEffect(() => {
    onAccountsDataChange?.({
      cuentas: accountsPayload,
      gastos: accountsPayload,
      cuentas_raw: safeRows,
      cuentas_subtotal15: centsToMoney(totals.neto15C),
      cuentas_subtotal5: centsToMoney(totals.neto5C),
      cuentas_subtotal0: centsToMoney(totals.neto0C),
      cuentas_subtotalNoObj: centsToMoney(totals.netoNoObjC),
      cuentas_descuento_total: centsToMoney(totals.descuentoC),
      cuentas_iva15: centsToMoney(totals.iva15C),
      cuentas_iva5: centsToMoney(totals.iva5C),
      cuentas_total: centsToMoney(totals.totalC),
    });
  }, [accountsPayload, safeRows, totals, onAccountsDataChange]);

  useEffect(() => {
    const loadAccounts = async () => {
      setLoading(true);
      setErrorMessage("");

      try {
        const res = await fetch("/cuentas/movimiento/", {
          credentials: "include",
          headers: {
            ...getAuthHeaders(),
          },
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          setAccounts([]);
          setErrorMessage(data.error || "No se pudieron cargar las cuentas.");
          return;
        }

        const lista = normalizeAccounts(data).filter(
          (c) =>
            c.active !== false &&
            c.activa !== false &&
            c.isMovement !== false &&
            c.visible !== false
        );

        setAccounts(lista);
      } catch {
        setAccounts([]);
        setErrorMessage("No se pudieron cargar las cuentas.");
      } finally {
        setLoading(false);
      }
    };

    loadAccounts();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!searchBoxRef.current) return;
      if (!searchBoxRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, []);

  const filteredAccounts = useMemo(() => {
    const q = busqueda.trim().toLowerCase();

    if (q.length < 2) return [];

    return accounts
      .filter((c) => {
        const code = String(c.code || c.codigo || "").toLowerCase();
        const name = String(c.name || c.nombre || "").toLowerCase();
        const group = String(c.group || c.grupo || "").toLowerCase();
        return code.includes(q) || name.includes(q) || group.includes(q);
      })
      .slice(0, 25);
  }, [accounts, busqueda]);

  const accountLabel = (account) => {
    if (!account) return "";
    const code = account.code || account.codigo || "";
    const name = account.name || account.nombre || "";
    return `${code} ${name}`.trim();
  };

  const handleSelectAccount = (account) => {
    setSelectedAccount(account);
    setBusqueda(accountLabel(account));
    setShowResults(false);
  };

  const clearSearch = () => {
    setSelectedAccount(null);
    setBusqueda("");
    setShowResults(false);
  };

  const handleAdd = () => {
    if (!selectedAccount) {
      setErrorMessage("Debe seleccionar una cuenta.");
      return;
    }

    const valorNum = toNum(valor);

    if (valorNum <= 0) {
      setErrorMessage("Debe ingresar un valor mayor a cero.");
      return;
    }

    const cuentaId = selectedAccount.id;
    const cuentaCodigo = selectedAccount.code || selectedAccount.codigo || "";
    const cuentaNombre = selectedAccount.name || selectedAccount.nombre || "";
    const descFinal = descripcionLinea.trim() || cuentaNombre || "";

    setRows([
      ...safeRows,
      {
        cuenta_id: cuentaId,
        cuenta_codigo: cuentaCodigo,
        cuenta_nombre: cuentaNombre,
        descripcion: descFinal,
        valor: normalizeDecimalInput(valor),
        iva_porcentaje: Number(ivaPorcentaje),
        descuentoPct: descuentoPct === "" ? "0" : String(Number(descuentoPct || 0)),
      },
    ]);

    setSelectedAccount(null);
    setBusqueda("");
    setDescripcionLinea("");
    setValor("");
    setIvaPorcentaje(15);
    setDescuentoPct("0");
    setShowResults(false);
    setErrorMessage("");
  };

  const handleDelete = (idx) => {
    setRows(safeRows.filter((_, i) => i !== idx));
  };

  const handleFieldChange = (idx, field, value) => {
    setRows(
      safeRows.map((r, i) => {
        if (i !== idx) return r;

        if (field === "valor" || field === "descuentoPct") {
          const v = normalizeDecimalInput(value);
          return { ...r, [field]: v };
        }

        if (field === "iva_porcentaje") {
          return { ...r, iva_porcentaje: Number(value) };
        }

        return { ...r, [field]: value };
      })
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      {errorMessage && (
        <Typography color="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
      )}

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={5}>
          <Box ref={searchBoxRef} sx={{ position: "relative", width: "100%" }}>
            <TextField
              size="small"
              placeholder="Buscar cuenta por código o nombre"
              fullWidth
              value={busqueda}
              onFocus={() => {
                if (busqueda.trim().length >= 2) {
                  setShowResults(true);
                }
              }}
              onChange={(e) => {
                const value = e.target.value;
                setBusqueda(value);
                setSelectedAccount(null);
                setShowResults(value.trim().length >= 2);
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setShowResults(false);
                  e.currentTarget.blur();
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: 3,
                  bgcolor: "#fff",
                  pr: "38px",
                },
                "& .MuiOutlinedInput-input": {
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "#64748b" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {loading ? <CircularProgress size={18} /> : null}
                    {!!busqueda && (
                      <IconButton
                        onClick={clearSearch}
                        edge="end"
                        size="small"
                        sx={{ mr: -0.5 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />

            {showResults && (
              <Paper
                elevation={4}
                sx={{
                  position: "absolute",
                  top: "44px",
                  left: 0,
                  right: 0,
                  zIndex: 5,
                  maxHeight: 260,
                  overflowY: "auto",
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                }}
              >
                {filteredAccounts.length > 0 ? (
                  filteredAccounts.map((account) => (
                    <Box
                      key={account.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelectAccount(account);
                      }}
                      sx={{
                        px: 1.5,
                        py: 1,
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: "#f1f5f9",
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                        {account.code || account.codigo || ""} {account.name || account.nombre || ""}
                      </Typography>
                      {(account.group || account.grupo) && (
                        <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                          {account.group || account.grupo}
                        </Typography>
                      )}
                    </Box>
                  ))
                ) : (
                  <Box sx={{ px: 1.5, py: 1.2 }}>
                    <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                      No se han encontrado cuentas
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            label="Detalle"
            size="small"
            fullWidth
            value={descripcionLinea}
            onFocus={() => setShowResults(false)}
            onChange={(e) => setDescripcionLinea(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <TextField
            label="Valor sin IVA"
            size="small"
            fullWidth
            value={valor}
            onFocus={() => setShowResults(false)}
            onChange={(e) => setValor(normalizeDecimalInput(e.target.value))}
            inputProps={{ inputMode: "decimal" }}
          />
        </Grid>

        <Grid item xs={12} md={1}>
          <Select
            value={ivaPorcentaje}
            onOpen={() => setShowResults(false)}
            onChange={(e) => setIvaPorcentaje(Number(e.target.value))}
            size="small"
            fullWidth
          >
            <MenuItem value={0}>0%</MenuItem>
            <MenuItem value={5}>5%</MenuItem>
            <MenuItem value={15}>15%</MenuItem>
            <MenuItem value={999}>No objeto</MenuItem>
          </Select>
        </Grid>

        <Grid item xs={12} md={1}>
          <TextField
            label="Desc. %"
            size="small"
            fullWidth
            value={descuentoPct}
            onFocus={() => setShowResults(false)}
            onChange={(e) => setDescuentoPct(normalizeDecimalInput(e.target.value))}
            inputProps={{ inputMode: "decimal" }}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineRoundedIcon />}
            onClick={handleAdd}
            sx={{
              bgcolor: "#1565C0",
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            Añadir cuenta
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1565C0" }}>
              <TableCell sx={{ color: "#fff" }}>Cuenta</TableCell>
              <TableCell sx={{ color: "#fff" }}>Detalle</TableCell>
              <TableCell sx={{ color: "#fff" }}>Valor</TableCell>
              <TableCell sx={{ color: "#fff" }}>IVA (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc. (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc. ($)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Subtotal</TableCell>
              <TableCell sx={{ color: "#fff" }}>Total</TableCell>
              <TableCell sx={{ color: "#fff" }}>Eliminar</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {safeRows.map((r, i) => {
              const calc = calcRow(r);

              return (
                <TableRow key={i}>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {r.cuenta_codigo ? `${r.cuenta_codigo} ` : ""}
                      {r.cuenta_nombre || ""}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ minWidth: 280 }}>
                    <TextField
                      value={r.descripcion || ""}
                      onFocus={() => setShowResults(false)}
                      onChange={(e) => handleFieldChange(i, "descripcion", e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={6}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.valor}
                      onFocus={() => setShowResults(false)}
                      onChange={(e) => handleFieldChange(i, "valor", e.target.value)}
                      sx={{ width: 110 }}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </TableCell>

                  <TableCell>
                    <Select
                      value={r.iva_porcentaje}
                      onOpen={() => setShowResults(false)}
                      onChange={(e) => handleFieldChange(i, "iva_porcentaje", e.target.value)}
                      sx={{ width: 100 }}
                    >
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={5}>5%</MenuItem>
                      <MenuItem value={15}>15%</MenuItem>
                      <MenuItem value={999}>No objeto</MenuItem>
                    </Select>
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.descuentoPct}
                      onFocus={() => setShowResults(false)}
                      onChange={(e) => handleFieldChange(i, "descuentoPct", e.target.value)}
                      sx={{ width: 80 }}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </TableCell>

                  <TableCell>${formatMoney(calc.descTotal)}</TableCell>
                  <TableCell>${formatMoney(calc.neto)}</TableCell>
                  <TableCell>${formatMoney(calc.totalLinea)}</TableCell>

                  <TableCell>
                    <IconButton onClick={() => handleDelete(i)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}